import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createClient } from "@west-tennessee-consulting/ui";
import { useUniversalAuth } from "@west-tennessee-consulting/ui";
import { toast } from "sonner";
import type { User } from "@/lib/types/user";

type UpdateUserContext = {
  previousCurrentUser: User | undefined;
  previousUser: User | undefined;
  previousAllUsers: User[] | undefined;
  previousPaginated: { data: User[] } | undefined;
};

export function useUpdateUser() {
  const queryClient = useQueryClient();
  const { appUser } = useUniversalAuth();
  const supabase = createClient();

  return useMutation<User, Error, { userId: string; updates: Partial<User> }, UpdateUserContext>({
    mutationFn: async ({ userId, updates }) => {
      if (!appUser) {
        throw new Error("Authentication required");
      }

      // Users can only update their own profile unless they're admin/employee/CRM
      const userRole = appUser.roles?.name || appUser.role;
      if (
        userId !== appUser.id &&
        !["wtc_employee", "wtc_admin", "wtc_crm"].includes(userRole || "")
      ) {
        throw new Error("Insufficient permissions");
      }

      const { data, error } = await supabase
        .from("app_users")
        .update(updates)
        .eq("id", userId)
        .select()
        .single();

      if (error) {
        throw new Error(`Failed to update user: ${error.message}`);
      }

      return data;
    },
    onMutate: async ({ userId, updates }): Promise<UpdateUserContext> => {
      await queryClient.cancelQueries({ queryKey: ["current-user", userId] });
      await queryClient.cancelQueries({ queryKey: ["user", userId] });
      await queryClient.cancelQueries({ queryKey: ["all-users"] });
      await queryClient.cancelQueries({ queryKey: ["users-paginated"] });

      const previousCurrentUser = queryClient.getQueryData<User>(["current-user", userId]);
      const previousUser = queryClient.getQueryData<User>(["user", userId]);
      const previousAllUsers = queryClient.getQueryData<User[]>(["all-users"]);
      const previousPaginated = queryClient.getQueryData<{ data: User[] }>(["users-paginated"]);

      queryClient.setQueryData(["user", userId], (old: User | undefined) => {
        if (!old) return old;
        return { ...old, ...updates };
      });

      queryClient.setQueryData(["current-user", userId], (old: User | undefined) => {
        if (!old) return old;
        return { ...old, ...updates };
      });

      queryClient.setQueryData(["all-users"], (old: User[] | undefined) => {
        if (!old) return old;
        return old.map((user) => (user.id === userId ? { ...user, ...updates } : user));
      });

      queryClient.setQueryData(["users-paginated"], (old: { data: User[] } | undefined) => {
        if (!old) return old;
        return {
          ...old,
          data: old.data?.map((user: User) =>
            user.id === userId ? { ...user, ...updates } : user
          ),
        };
      });

      return { previousCurrentUser, previousUser, previousAllUsers, previousPaginated };
    },
    onSuccess: (data) => {
      queryClient.setQueryData(["user", data.id], data);
      queryClient.setQueryData(["current-user", data.id], data);

      queryClient.setQueryData(["all-users"], (old: User[] | undefined) => {
        if (!old) return old;
        return old.map((user) => (user.id === data.id ? data : user));
      });

      queryClient.setQueryData(["users-paginated"], (old: { data: User[] } | undefined) => {
        if (!old) return old;
        return {
          ...old,
          data: old.data?.map((user: User) => (user.id === data.id ? data : user)),
        };
      });

      toast.success("User updated successfully");
    },
    onError: (error, _variables, onMutateResult: UpdateUserContext | undefined) => {
      if (onMutateResult?.previousCurrentUser) {
        queryClient.setQueryData(["current-user", _variables.userId], onMutateResult.previousCurrentUser);
      }
      if (onMutateResult?.previousUser) {
        queryClient.setQueryData(["user", _variables.userId], onMutateResult.previousUser);
      }
      if (onMutateResult?.previousAllUsers) {
        queryClient.setQueryData(["all-users"], onMutateResult.previousAllUsers);
      }
      if (onMutateResult?.previousPaginated) {
        queryClient.setQueryData(["users-paginated"], onMutateResult.previousPaginated);
      }

      toast.error(error.message || "Failed to update user");
    },
  });
}

export function useCreateUser() {
  const queryClient = useQueryClient();
  const { appUser } = useUniversalAuth();
  const supabase = createClient();

  return useMutation<User, Error, Partial<User>>({
    mutationFn: async (userData) => {
      if (!appUser) {
        throw new Error("Authentication required");
      }

      // Only admins, employees, and CRM users can create users
      const userRole = appUser.roles?.name || appUser.role;
      if (!["wtc_employee", "wtc_admin", "wtc_crm"].includes(userRole || "")) {
        throw new Error("Insufficient permissions");
      }

      // If userData contains a role name, convert it to role_id
      const processedUserData = { ...userData };
      if (userData.role && !userData.role_id) {
        const { data: roleData, error: roleError } = await supabase
          .from("roles")
          .select("id")
          .eq("name", userData.role)
          .single();

        if (roleError) {
          throw new Error(`Invalid role: ${userData.role}`);
        }

        processedUserData.role_id = roleData.id;
        delete processedUserData.role;
      }

      const { data, error } = await supabase
        .from("app_users")
        .insert(processedUserData)
        .select(
          `
          *,
          roles:role_id (
            id,
            name,
            display_name,
            description,
            permissions,
            is_active
          )
        `
        )
        .single();

      if (error) {
        throw new Error(`Failed to create user: ${error.message}`);
      }

      return data;
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ["all-users"] });
      void queryClient.invalidateQueries({ queryKey: ["user", data.id] });
    },
  });
}

export function useDeleteUser() {
  const queryClient = useQueryClient();
  const { appUser } = useUniversalAuth();
  const supabase = createClient();

  return useMutation<void, Error, string>({
    mutationFn: async (userId) => {
      if (!appUser) {
        throw new Error("Authentication required");
      }

      // Only admins can delete users
      const userRole = appUser.roles?.name || appUser.role;
      if (userRole !== "wtc_admin") {
        throw new Error("Insufficient permissions");
      }

      const { error } = await supabase.from("app_users").delete().eq("id", userId);

      if (error) {
        throw new Error(`Failed to delete user: ${error.message}`);
      }
    },
    onSuccess: (_, userId) => {
      void queryClient.invalidateQueries({ queryKey: ["all-users"] });
      void queryClient.invalidateQueries({ queryKey: ["user", userId] });
    },
  });
}
